import { IonApp, setupIonicReact } from '@ionic/react';
/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/typography.css';
import React from 'react';

import { useAccount } from './hooks/useAccount';
import { useColorScheme } from './hooks/useColorScheme';
import { useUserGeo } from './hooks/useUserGeo';
import { ColorSchemeListener } from './listeners/ColorSchemeListener';
import { colorScheme } from './modules/colorScheme';
import { queryParamsModule } from './modules/queryParams';
import { webCampaign } from './modules/webCampaign';
import OnboardingFlowPage from './pages/Onboarding/Onboarding';
import OnboardingPaywall from './pages/Paywall/Paywall';
import PostPaywallName from './pages/PostPaywallName/PostPaywallName';
import SuccessPaymentPage from './pages/SuccessPayment/SuccessPayment';
import { userService } from './services/user';
/* Theme variables */
import './theme/layout.css';
import './theme/variables-dark.css';
import './theme/variables.css';

setupIonicReact();

const App: React.FC = () => {
  const { signIn, user, currentUser, setSelectedPlanId } = useAccount();
  const { setTheme } = useColorScheme();
  const { initGeo } = useUserGeo();

  const init = async () => {
    initGeo();
    setSelectedPlanId(webCampaign.setup.defaultPlan);
    setTheme(colorScheme.getTheme());

    const userId = queryParamsModule.getParam('user_id');
    const user = await signIn(userId);

    const campaignId = webCampaign.getKey();
    const userCampaignId = user?.onboarding_data.campaign_id;

    if (userCampaignId) {
      /**
       * If user has a campaign_id in the onboarding_data and it's different from the current campaign_id
       * we need to update the user's campaign_id to the current campaign_id and load the correct onboarding flow
       */
      if (campaignId !== userCampaignId) {
        queryParamsModule.setParam(
          webCampaign.QUERY_PARAM_KEY.WEB_CAMPAIGN,
          userCampaignId,
        );
        webCampaign.removeServiceQueryParams();

        window.location.assign(window.location.href);
      }
    } else {
      if (!webCampaign.isDomainDefaultCampaign()) {
        queryParamsModule.setParam(
          webCampaign.QUERY_PARAM_KEY.WEB_CAMPAIGN,
          campaignId,
        );
      }
      webCampaign.removeServiceQueryParams();

      userService.createOrUpdate({
        onboarding_data: {
          campaign_id: campaignId,
        },
      });
    }

    if (user) {
      queryParamsModule.setParam('user_id', user.id);
    }
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasAccessToSuccessPage =
    !!currentUser.subscription ||
    (!!user?.onboarding_data.onboarded && webCampaign.setup.paywall === null);
  const hasAccessToPaymentPage = !!user?.onboarding_data.onboarded;

  const shouldShowPostPaywallName =
    hasAccessToSuccessPage &&
    webCampaign.setup.hasNameAfterPaywall &&
    !user?.name;

  if (shouldShowPostPaywallName) {
    return (
      <IonApp>
        <ColorSchemeListener />
        <PostPaywallName />
      </IonApp>
    );
  }

  if (hasAccessToSuccessPage) {
    return (
      <IonApp>
        <ColorSchemeListener />
        <SuccessPaymentPage />
      </IonApp>
    );
  }

  if (hasAccessToPaymentPage) {
    return (
      <IonApp>
        <ColorSchemeListener />
        <OnboardingPaywall />
      </IonApp>
    );
  }

  return (
    <IonApp>
      <ColorSchemeListener />
      <OnboardingFlowPage />
    </IonApp>
  );
};

export default App;
